import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const CertificateView = () => {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCertificate = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/hackathon/certificate/${certId}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Certificate not found');
          }
          throw new Error('Failed to fetch certificate');
        }
        const data = await response.json();
        setCertificate(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificate();
  }, [certId]);

  const handlePrint = () => {
    window.print();
  };

  const shareOnLinkedIn = () => {
    const url = window.location.href;
    const text = `I'm excited to share that I received a certificate for ${certificate.hackathon_name}! 🎉`;
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const shareOnTwitter = () => {
    const url = window.location.href;
    const text = `I'm excited to share that I received a certificate for ${certificate.hackathon_name}! 🎉 @sciFi_ai`;
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      '_blank'
    );
  };

  const getCertificateTitle = () => {
    if (!certificate) return '';

    if (certificate.certificate_type === 'winner') {
      const medals = ['🥇', '🥈', '🥉'];
      return `${medals[certificate.rank - 1]} ${certificate.rank === 1 ? '1st' : certificate.rank === 2 ? '2nd' : '3rd'} Place Winner`;
    } else if (certificate.certificate_type === 'finalist') {
      return `Top ${certificate.rank} Finalist`;
    } else {
      return 'Certificate of Participation';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Certificate Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/hackathon/leaderboard')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Leaderboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
          .certificate-container {
            box-shadow: none !important;
            max-width: 100% !important;
            padding: 0 !important;
          }
        }
      `}</style>

      {/* Header with Actions (No Print) */}
      <div className="no-print bg-white border-b border-gray-200 py-4 px-6 flex justify-between items-center">
        <button
          onClick={() => navigate('/hackathon/leaderboard')}
          className="text-blue-600 hover:text-blue-700 font-medium flex items-center"
        >
          ← Back to Leaderboard
        </button>
        <div className="flex gap-3">
          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            🖨️ Print / Save as PDF
          </button>
          {/* <button
            onClick={shareOnLinkedIn}
            className="bg-[#0077B5] text-white px-4 py-2 rounded-lg hover:bg-[#006399] transition-colors flex items-center gap-2"
          >
            in Share on LinkedIn
          </button> */}
          <button
            onClick={shareOnTwitter}
            className="bg-[#1DA1F2] text-white px-4 py-2 rounded-lg hover:bg-[#1a8cd8] transition-colors flex items-center gap-2"
          >
            𝕏 Share on Twitter
          </button>
        </div>
      </div>

      {/* Certificate Container */}
      <div className="flex items-center justify-center p-8">
        <div className="certificate-container bg-white rounded-lg shadow-2xl p-16 max-w-4xl w-full border-8 border-blue-600">

          {/* Header with Logo */}
          <div className="text-center mb-8">
            <img
              src="/logo/scifi_logo_transprent.png"
              alt="sciFi Logo"
              className="h-16 mx-auto mb-4"
            />
            {/* <h1 className="text-5xl font-bold text-blue-600 mb-2">sciFi</h1> */}
            <p className="text-gray-600 text-lg">Jupyter-style AI-Powered Notebooks</p>
          </div>

          {/* Certificate Title */}
          <div className="border-t-2 border-b-2 border-blue-600 py-6 my-8">
            <h2 className="text-3xl font-bold text-center text-gray-800">
              CERTIFICATE OF ACHIEVEMENT
            </h2>
          </div>

          {/* Main Content */}
          <div className="text-center mb-8">
            <p className="text-gray-600 text-lg mb-4">This certificate is proudly presented to</p>

            <h3 className="text-4xl font-bold text-gray-900 mb-6 border-b-2 border-gray-300 pb-2 inline-block px-8">
              {certificate.participant_name}
            </h3>

            <p className="text-gray-600 text-lg mb-6">
              for successfully completing the
            </p>

            <h4 className="text-2xl font-bold text-blue-600 mb-4">
              {certificate.hackathon_name}
            </h4>

            <p className="text-gray-500 text-md mb-6">
              {certificate.hackathon_date_range}
            </p>

            {/* Achievement Badge */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 my-8 border-2 border-blue-300">
              <h5 className="text-2xl font-bold text-blue-700 mb-2">
                {getCertificateTitle()}
              </h5>

              {certificate.certificate_type === 'winner' && certificate.composite_score && (
                <p className="text-lg text-gray-700">
                  Score: <span className="font-bold text-blue-600">{certificate.composite_score}</span>
                </p>
              )}

              {certificate.certificate_type === 'finalist' && (
                <p className="text-md text-gray-600">
                  Ranked in the Top 10 Finalists
                </p>
              )}

              {certificate.certificate_type === 'participant' && (
                <p className="text-md text-gray-600">
                  Thank you for participating in this amazing hackathon!
                </p>
              )}
            </div>
          </div>

          {/* Footer with Certificate ID and Date */}
          <div className="border-t-2 border-gray-200 pt-6 mt-8">
            <div className="flex justify-between items-center">
              <div className="text-left">
                <p className="text-gray-500 text-sm">Certificate ID</p>
                <p className="text-gray-800 font-mono font-bold">{certificate.cert_id}</p>
              </div>

              <div className="text-right">
                <p className="text-gray-500 text-sm">Issue Date</p>
                <p className="text-gray-800 font-bold">
                  {new Date(certificate.issued_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Verification Note */}
          <div className="text-center mt-6 text-sm text-gray-500">
            <p>Verify this certificate at: <span className="font-mono text-blue-600">{window.location.href}</span></p>
          </div>
        </div>
      </div>

      {/* Footer (No Print) */}
      <div className="no-print text-center py-8 text-gray-500 text-sm">
        <p>Powered by sciFi © 2026 | Celebrating Innovation & Excellence</p>
      </div>
    </div>
  );
};

export default CertificateView;
